import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Defs, RadialGradient, Rect, Stop } from 'react-native-svg';
import { Text } from './Texte';
import { Info, Sparkles } from './icons';
import { t } from '../i18n';
import { colors, radius, spacing } from '../theme/tokens';
import type { CvChange, CvChangeTag } from '../types';

export interface BilanModificationsProps {
  changes: CvChange[];
  testID?: string;
}

/** Ordre d'affichage des étiquettes : le titre d'abord, puis du plus visible au plus fin. */
const ORDRE: CvChangeTag[] = ['title', 'impact', 'action', 'keywords', 'clarity'];

/**
 * Haut de l'écran de revue : le nombre de modifications dans une tuile de verre, sur le même panneau
 * bleu nuit que l'attente, le décompte par nature, puis la consigne.
 *
 * Le décompte vient des étiquettes des modifications reçues, jamais d'une valeur fixe.
 */
export function BilanModifications({ changes, testID = 'bilan-modifications' }: BilanModificationsProps) {
  const total = changes.length;
  const parNature = useMemo(() => {
    const n = new Map<CvChangeTag, number>();
    changes.forEach((c) => n.set(c.tag, (n.get(c.tag) ?? 0) + 1));
    return ORDRE.filter((tag) => n.has(tag)).map((tag) => ({ tag, n: n.get(tag) ?? 0 }));
  }, [changes]);

  return (
    <View style={styles.conteneur}>
      <View testID={testID} style={styles.panneau}>
        <LinearGradient
          colors={['#0E2A72', colors.night, '#0A1640']}
          start={{ x: 0.45, y: 0 }}
          end={{ x: 0.55, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <Svg width={190} height={190} style={styles.lueur} pointerEvents="none">
          <Defs>
            <RadialGradient id={`bilan-${testID}`} cx="50%" cy="50%" r="50%">
              <Stop offset="0" stopColor="#785CFF" stopOpacity={0.42} />
              <Stop offset="0.48" stopColor={colors.cyan} stopOpacity={0.14} />
              <Stop offset="0.72" stopColor={colors.cyan} stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Rect x="0" y="0" width="190" height="190" fill={`url(#bilan-${testID})`} />
        </Svg>

        <View
          style={styles.titreLigne}
          accessible
          accessibilityRole="header"
          accessibilityLabel={t('cvImprove.revue.titre', { count: total })}
        >
          <View style={styles.tuile}>
            <LinearGradient
              colors={['rgba(255,255,255,0.26)', 'rgba(255,255,255,0.06)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.verre}
            />
            <Text testID="cvimprove-revue-nombre" style={styles.nombre}>
              {total}
            </Text>
            <View style={styles.etincelle}>
              <Sparkles size={16} color={colors.white} fill={colors.white} strokeWidth={0} />
            </View>
          </View>
          <Text testID="cvimprove-revue-titre" style={styles.titre}>
            {t('cvImprove.revue.libelle', { count: total })}
          </Text>
        </View>

        <View style={styles.natures}>
          {parNature.map(({ tag, n }) => (
            <View key={tag} testID={`cvimprove-revue-nature-${tag}`} style={styles.nature}>
              <Text style={styles.natureTexte}>{`${t(`cvImprove.tags.${tag}`)} ${n}`}</Text>
            </View>
          ))}
        </View>
      </View>

      <View testID="cvimprove-revue-consigne" style={styles.consigne}>
        <View style={styles.consigneIcone}>
          <Info size={18} color={colors.electricDeep} strokeWidth={2.2} />
        </View>
        <View style={styles.consigneTextes}>
          <Text style={styles.consigneTitre}>{t('cvImprove.revue.aideTitre')}</Text>
          <Text style={styles.consigneSuite}>{t('cvImprove.revue.aideSuite')}</Text>
          <Text testID="cvimprove-revue-score" style={styles.consigneNote}>
            {t('cvImprove.revue.score')}
          </Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  conteneur: { gap: spacing.md + 2 },
  panneau: {
    borderRadius: 28,
    overflow: 'hidden',
    padding: spacing.lg,
    gap: spacing.md + 2,
    shadowColor: colors.night,
    shadowOpacity: 0.28,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 12 },
    elevation: 8,
  },
  lueur: { position: 'absolute', left: 6, top: 6 },
  titreLigne: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg },
  tuile: { width: 68, height: 68, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  verre: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.36)',
  },
  nombre: { fontSize: 40, lineHeight: 46, fontWeight: '800', letterSpacing: -1.5, color: '#EAF0FF' },
  etincelle: { position: 'absolute', top: -6, right: -6 },
  titre: { flex: 1, fontSize: 22, lineHeight: 26, fontWeight: '800', letterSpacing: -0.4, color: colors.white },
  natures: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  nature: {
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 14,
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.24)',
  },
  natureTexte: { fontSize: 12.5, lineHeight: 16, fontWeight: '700', color: colors.white },
  consigne: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
    paddingHorizontal: spacing.md + 2,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.white,
    backgroundColor: 'rgba(255, 255, 255, 0.72)',
  },
  consigneIcone: {
    width: 32,
    height: 32,
    borderRadius: radius.md - 1,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  consigneTextes: { flex: 1, gap: 2 },
  consigneTitre: { fontSize: 14, lineHeight: 19, fontWeight: '700', color: colors.night },
  consigneSuite: { fontSize: 13, lineHeight: 18, color: colors.grey700 },
  consigneNote: { fontSize: 12, lineHeight: 16, color: colors.grey500 },
});
